/* eslint-disable prefer-destructuring */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Chart } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Typography from '@mui/material/Typography';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoTooltip from '../utility/InfoTooltip';
import { datasetProfiles, allPercentDataKeys, allPercentDataKeyLabels, allCountDataKeys, uniqueColorsArray } from './datasetProfiles';
import { openModal } from '../../reducers/modal';
import { modalTypes } from '../modal/Modal';

Chart.register(ChartDataLabels);

const numberFormat = new Intl.NumberFormat('en-US');

const useStyles = ({ mobile, activeDemographicDatasetCount }) => ({
  graphWrapper: {
    position: 'relative',
    width: '100%',
    marginBottom: mobile ? 10 : 0,
    backgroundColor: 'rgb(34, 70, 127)',
    height: '100%',
  },
  chartWrapper: {
    width: '100%',
    height: mobile ? '60vh' : (`${(activeDemographicDatasetCount * 80)}px`),
  },
});

const StackedBarChart = ({ mobile }) => {
  const barChart = useRef(null);
  const dispatch = useDispatch();
  const selection = useSelector((state) => state.mainControls.selection);
  const level = useSelector((state) => state.mainControls.level);
  const statewideData = useSelector((state) => state.statewideData?.feature?.properties);
  const [chartData, setChartData] = useState();
  const [activeData, setActiveData] = useState();
  const [chartOptions, setChartOptions] = useState();
  const [showDatasets, setShowDatasets] = useState({
    'Service Coverage': true,
    'Maximum Speed': true,
    'Number of Providers': true,
    'Internet Subscriptions': true,
    'Household Income': true,
    Race: true,
    Age: true,
  });
  const classes = useStyles({
    mobile,
    activeDemographicDatasetCount: Object
      .values(showDatasets)
      .filter((value) => value)
      .length,
  });

  useEffect(() => {
    if (selection) {
      setActiveData(selection);
    } else if (statewideData) {
      setActiveData(statewideData);
    }
  }, [selection, statewideData]);

  useEffect(() => {
    if (activeData) {
      const updatedShowDatasets = { ...showDatasets };
      datasetProfiles.forEach(({ label, percentKeys }) => {
        const datasetPercentValues = percentKeys.map((key) => activeData[key]);
        // if dataset has no data flag it as no show
        if (datasetPercentValues.every((percent) => percent === 0)) {
          updatedShowDatasets[label] = false;
        } else updatedShowDatasets[label] = true;
      });
      setShowDatasets(updatedShowDatasets);
    }
  }, [activeData]);

  useEffect(() => {
    if (activeData && level) {
      const datasets = [];
      // data shape for graph requires individual datasets for each datapoint
      allPercentDataKeys.forEach((key, i) => {
        let pushDataset = true;
        datasetProfiles.forEach(({ label, percentKeys }) => {
          if (percentKeys.includes(key)) {
            if (!showDatasets[label]) pushDataset = false;
          }
        });
        if (pushDataset) {
          datasets.push({
            label: allPercentDataKeyLabels[i],
            data: datasetProfiles.map((dataset) => {
              if (dataset.percentKeys.includes(key)) return (activeData[key] * 100).toFixed(2);
              return 0;
            }),
            backgroundColor: uniqueColorsArray[i],
            barThickness: 35,
          });
        }
      });
      setChartData({
        labels: datasetProfiles.filter(({ label }) => showDatasets[label]).map(({ label }) => label),
        datasets,
      });
    }
  }, [activeData, level, selection, showDatasets]);

  useEffect(() => {
    if (activeData) {
      setChartOptions({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0,
        },
        interaction: {
          mode: 'index',
          xAlign: 'center',
          position: 'nearest',
        },
        tooltips: {},
        layout: {
          padding: {
            top: 10,
            bottom: mobile ? 10 : 15,
            left: 15,
            right: 15,
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              color: '#fff',
            },
            min: 0,
            max: 100,
          },
          x1: {
            stacked: true,
            ticks: {
              color: '#fff',
            },
            min: 0,
            max: 100,
            position: 'top',
          },
          y: {
            stacked: true,
            grid: {
              display: false,
            },
            ticks: {
              color: '#fff',
              font: {
                size: 16,
              },
              // if client wants source in popup, but not axis title
              // callback: (value, index) => datasetProfiles[index].label.split('(')[0],
            },
          },
        },
        plugins: {
          title: { display: false },
          legend: { display: false },
          tooltip: {
            mode: 'index',
            callbacks: {
              label: ({ dataset, dataIndex }) => {
                // if (dataset.label === '> 150k') console.log({ dataset, dataIndex });
                const countIndex = allPercentDataKeyLabels.findIndex((label) => label === dataset.label);
                const count = activeData[allCountDataKeys[countIndex]];
                let label;
                if (count) label = `${dataset.label}: ${numberFormat.format(count)} (${dataset.data[dataIndex]}%)`;
                else label = `${dataset.label}: ${dataset.data[dataIndex]}%`;
                if (datasetProfiles[dataIndex].labels.includes(dataset.label)) return label;
              },
            },
          },
          datalabels: {
            color: '#fff',
            formatter: (value, context) => {
              if (value >= 30) return context.dataset.label;
              return '';
            },
          },
        },
      });
    }
  }, [activeData, mobile]);

  if ((selection || statewideData) && chartData && chartOptions) {
    return (
      <div
        style={classes.graphWrapper}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: '1rem',
            paddingTop: '0.3rem',
            height: '12%',
          }}
        >
          <InfoTooltip
            content={(
              <div style={{ whiteSpace: 'pre-line' }}>
                <strong style={{ fontSize: 20 }}>Data Sources for Charts</strong>
                <br />
                <u>Provider Reported Data:</u>
                <br />
                Service Coverage, Maximum Speed, and Number of Providers from data submitted directly to MBI or from FCC BDC filing, current as of 6/30/22 or later.
                <br />
                <u>US Census Bureau Data:</u>
                <br />
                Internet Subscriptions, Household Income, and Age from 2021 ACS 5-year.
                Race from 2020 Decennial Census.
                <br />
                This data is not available for some municipalities or any census blocks. Select the census tract for these areas
              </div>
            )}
          />
          <Typography component="h6" sx={{ fontWeight: 500, fontSize: '20px', marginLeft: '0.5rem' }}>Broadband & Digital Equity Indicators</Typography>
          <Tooltip title="Download demographics table for entire state">
            <IconButton
              sx={{
                color: '#8dabf4',
                marginLeft: 'auto',
              }}
              onClick={() => dispatch(openModal({ type: modalTypes.CONFIRM_DOWNLOAD }))}
            >
              <DownloadForOfflineIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div style={{ height: '88%', overflowY: 'auto' }}>
          <div style={classes.chartWrapper}>
            <Bar
              ref={barChart}
              data={chartData}
              options={chartOptions}
            />
          </div>
        </div>
      </div>
    );
  }
};

export default StackedBarChart;
